import { useEffect, useRef } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { parseCvFile, parseCvText } from "../../lib/onboarding-api";
import {
  useResumeImportComposer,
  type ResumeComposerDraft,
  type ResumeImportComposerParsed,
} from "./use-resume-import-composer";
import { SparkleGlyph } from "../upgrade/UpgradeHeroIcon";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { useResumeDictation } from "../../hooks/useResumeDictation";
import { colors } from "@tabbio/tokens";
import { NovaListening } from "../voice/NovaListening.web";
import { useFileDropZone } from "../../hooks/useFileDropZone.web";
import { RESUME_FILE_ACCEPT } from "../../lib/file-drop";
import { useAiParseProgress } from "../onboarding/aiParseProgress";
import { trackProductEvent } from "../../lib/product-analytics";

export type { ResumeImportComposerParsed };

type ResumeImportComposerProps = {
  title: string;
  subtitle: string;
  placeholder: string;
  parseFailedTitle: string;
  parseFailedMessage: string;
  onParsed: (result: ResumeImportComposerParsed) => void | Promise<void>;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  hideSecondaryActionWhenBusy?: boolean;
  disabled?: boolean;
  showIntro?: boolean;
  analyticsContext?: string;
  skipEnrichment?: boolean;
  /**
   * Control rendered in the toolbar beside the attach button (onboarding uses
   * it for the CV-preset pill). Opt-in so the other composer call sites keep
   * their current two-button toolbar.
   */
  toolbarAccessory?: React.ReactNode;
  /**
   * Strip pinned above the textarea, inside the composer box (onboarding uses
   * it for the "preset applied" chip). Hidden while parsing, like the rest of
   * the composer chrome.
   */
  banner?: React.ReactNode;
  /**
   * Start reading a file the moment it is attached or dropped. For a surface
   * whose real submit is a Continue button below the composer, waiting for the
   * send arrow leaves the attached CV in a dead end.
   */
  parseOnAttach?: boolean;
  /** Reports the parse round-trip so an owning button can show it. */
  onBusyChange?: (busy: boolean) => void;
  /** Reports unsent draft content so an owning button can submit it. */
  onDraftChange?: (draft: ResumeComposerDraft) => void;
  /** Receives a `submit()` the owning screen can call from its own button. */
  submitRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * The host is not ready for a read (it is still loading, or failed to load,
   * the CV the read would replace). The input shows but nothing can be sent,
   * and no wait appears, because nothing is being read. Unlike `disabled`.
   */
  inputLocked?: boolean;
};

export function ResumeImportProgress({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation("auth");
  const { step } = useAiParseProgress(isActive);
  const title = t(`onboarding.cvJourney.parseProgress.${step.key}.title`, {
    defaultValue: step.title,
  });
  const detail = t(`onboarding.cvJourney.parseProgress.${step.key}.detail`, {
    defaultValue: step.detail,
  });

  return (
    <div
      aria-live="polite"
      aria-busy="true"
      style={{
        padding: "32px 24px 28px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 18,
      }}
    >
      <SparkleGlyph size={34} />
      <div style={{ width: "100%", maxWidth: 360 }}>
        <div
          style={{
            marginBottom: 14,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            key={step.key}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 8,
              maxWidth: "100%",
              minHeight: 32,
              animation:
                "tabbio-progress-copy 180ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <span
              style={{
                minWidth: 0,
                fontSize: 15,
                lineHeight: "20px",
                fontWeight: 500,
                color: "var(--text-primary)",
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </span>
            <span
              className="tabbio-menu-badge"
              style={{
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {step.percent}%
            </span>
          </div>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={step.percent}
          style={{
            height: 6,
            borderRadius: 999,
            overflow: "hidden",
            background: "rgba(0,0,0,0.06)",
          }}
        >
          <div
            className="tabbio-ai-progress-fill"
            style={{
              width: `${step.percent}%`,
              height: "100%",
              borderRadius: 999,
              background: "var(--text-primary)",
              transition: "width 360ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        </div>
        <p
          key={step.key}
          style={{
            margin: "12px 0 0",
            minHeight: 36,
            maxWidth: 320,
            marginInline: "auto",
            fontSize: 13,
            lineHeight: "18px",
            color: "var(--text-secondary)",
            textAlign: "center",
            overflowWrap: "anywhere",
            animation:
              "tabbio-progress-copy 220ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {detail}
        </p>
      </div>
      <style>{`
        @keyframes tabbio-progress-copy {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes tabbio-progress-copy { from, to { transform: none; opacity: 1; } }
          .tabbio-ai-progress-fill {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export function ResumeImportComposer({
  title,
  subtitle,
  placeholder,
  parseFailedTitle,
  parseFailedMessage,
  onParsed,
  secondaryActionLabel,
  onSecondaryAction,
  hideSecondaryActionWhenBusy = false,
  disabled = false,
  showIntro = true,
  analyticsContext = "resume_import",
  skipEnrichment = false,
  toolbarAccessory,
  banner,
  parseOnAttach = false,
  onBusyChange,
  onDraftChange,
  submitRef,
  inputLocked = false,
}: ResumeImportComposerProps) {
  const { t, i18n } = useTranslation("auth");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const composer = useResumeImportComposer<File>({
    t,
    disabled,
    parseOnAttach,
    locked: inputLocked,
    onBusyChange,
    onDraftChange,
    onParsed,
    parseFailedMessage,
    analyticsContext,
    describeFile: (picked) => ({
      type: picked.type,
      sizeKb: Math.round(picked.size / 1024),
    }),
    parse: ({ file: picked, text: pendingText }) =>
      picked
        ? parseCvFile(newFormData(picked), { skipEnrichment })
        : parseCvText(pendingText, { skipEnrichment }),
    // The recovery path drops the file; the native input has to be emptied too
    // or re-picking the same file fires no change event.
    onFileCleared: () => {
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });
  const { text, setText, file, parseError, setParseError, hasInput } = composer;
  const isParsing = composer.isSubmitting;
  const busy = composer.isParsing;

  const submitComposer = composer.submit;
  useEffect(() => {
    if (!submitRef) return;
    submitRef.current = () => void submitComposer();
    return () => {
      submitRef.current = null;
    };
  }, [submitComposer, submitRef]);

  const dictation = useResumeDictation({
    locale: i18n.language,
    enabled: !busy,
    onTranscript: (fullText) => {
      setText(fullText);
      setParseError(null);
    },
  });
  const canDictate = dictation.isSupported;

  const handleMicPress = () => {
    if (busy) return;
    if (dictation.isListening) {
      dictation.stop();
      return;
    }
    if (!canDictate) return;
    void dictation.start(text.trim());
  };

  const shouldShowSecondaryAction =
    !!secondaryActionLabel &&
    !!onSecondaryAction &&
    (!hideSecondaryActionWhenBusy || !busy);

  const clearSelectedFile = () => {
    composer.clearFile();
    setParseError(null);
  };

  const handleExtract = () => void composer.submit();

  const selectFile = (selected: File, origin: "picker" | "drop") =>
    composer.attachFile(selected, origin);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) {
      selectFile(selected, "picker");
      event.currentTarget.value = "";
    }
  };

  // Dropping a CV on the box used to make the browser open the file instead of
  // uploading it, because nothing handled dragover/drop (issue #254).
  const { isDraggingOver, dropZoneProps } = useFileDropZone({
    accept: RESUME_FILE_ACCEPT,
    disabled: isParsing || dictation.isListening || inputLocked,
    onFile: (dropped) => selectFile(dropped, "drop"),
    onRejected: () => setParseError(t("onboarding.cvJourney.unsupportedFile")),
  });

  return (
    <div style={{ width: "100%" }}>
      {showIntro ? (
        <>
          <h1
            style={{
              fontFamily: "'Google Sans', sans-serif",
              fontSize: 32,
              fontWeight: 400,
              lineHeight: "40px",
              letterSpacing: "-0.01em",
              textAlign: "center",
              color: "var(--text-primary)",
              margin: "0 0 8px",
            }}
          >
            {title}
          </h1>
          <p
            style={{
              fontSize: 15,
              lineHeight: "22px",
              textAlign: "center",
              color: "var(--text-secondary)",
              margin: "0 0 28px",
            }}
          >
            {subtitle}
          </p>
        </>
      ) : null}

      <div
        {...dropZoneProps}
        data-dragging={isDraggingOver ? "true" : undefined}
        style={{
          borderRadius: 24,
          background: "var(--input-bg)",
          border: isDraggingOver
            ? "1px solid var(--accent, #5e29ff)"
            : "1px solid var(--input-border)",
          boxShadow: "var(--gem-input-shadow)",
          overflow: "hidden",
        }}
      >
        {busy ? (
          <ResumeImportProgress isActive={busy} />
        ) : (
          // A host that is not ready keeps the input in view but closed:
          // nothing can be typed, attached or sent, and no wait shows.
          <fieldset
            disabled={inputLocked}
            style={{ margin: 0, padding: 0, border: 0, minWidth: 0, opacity: inputLocked ? 0.55 : 1 }}
          >
            {banner ? (
              <div style={{ padding: "14px 18px 0" }}>{banner}</div>
            ) : null}

            {file ? (
              <div style={{ padding: "14px 18px 0" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    borderRadius: 9999,
                    backgroundColor: "rgba(0,0,0,0.06)",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--text-secondary)",
                  }}
                >
                  <MaterialSymbol
                    name="attach_file"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                  {file.name}
                  <button
                    type="button"
                    onClick={() => {
                      clearSelectedFile();
                      setParseError(null);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      color: "var(--text-tertiary)",
                    }}
                    aria-label="Remove file"
                  >
                    <MaterialSymbol name="close" size={14} />
                  </button>
                </span>
              </div>
            ) : null}

            <div style={{ position: "relative" }}>
              <textarea
                value={text}
                onChange={(event) => {
                  setText(event.target.value);
                  setParseError(null);
                }}
                placeholder={dictation.isListening ? "" : placeholder}
                rows={4}
                // Locked while dictating so keyboard edits can't race the transcript,
                // which is rewritten from the base captured when the mic was tapped.
                readOnly={dictation.isListening}
                style={{
                  width: "100%",
                  padding: "18px 20px 8px",
                  fontSize: 15,
                  lineHeight: "22px",
                  color: "var(--text-primary)",
                  backgroundColor: "transparent",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
              {dictation.isListening && !text.trim() ? (
                // The status reads where the placeholder would, until there is text.
                <div
                  style={{
                    position: "absolute",
                    insetBlockStart: 18,
                    insetInlineStart: 20,
                    insetInlineEnd: 20,
                    pointerEvents: "none",
                  }}
                >
                  <NovaListening
                    state={dictation.status === "transcribing" ? "transcribing" : "listening"}
                    level={dictation.level}
                  />
                </div>
              ) : null}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 12px 12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={dictation.isListening}
                  aria-label="Attach file"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: dictation.isListening ? "default" : "pointer",
                    opacity: dictation.isListening ? 0.35 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--text-secondary)",
                    transition: "background-color 150ms, opacity 150ms",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    if (!dictation.isListening)
                      e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <MaterialSymbol name="attach_file" size={20} />
                </button>
                {toolbarAccessory}
                {dictation.isListening && text.trim() ? (
                  <NovaListening
                    state={dictation.status === "transcribing" ? "transcribing" : "listening"}
                    level={dictation.level}
                    style={{ marginInlineStart: 6 }}
                  />
                ) : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={RESUME_FILE_ACCEPT}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              {(() => {
                const listening = dictation.isListening;
                const showMic = !listening && !hasInput && canDictate;
                // Empty field on a browser without Speech Recognition (e.g. Firefox):
                // fall back to the original disabled send button instead of a mic that
                // can never work.
                const disabledEmpty = !listening && !hasInput && !canDictate;
                return (
                  <button
                    type="button"
                    onClick={
                      listening || showMic ? handleMicPress : handleExtract
                    }
                    disabled={disabledEmpty}
                    aria-label={
                      listening
                        ? t("chat.dictationStop", {
                            ns: "common",
                            defaultValue: "Stop dictation",
                          })
                        : showMic
                          ? t("chat.dictationStart", {
                              ns: "common",
                              defaultValue: "Start dictation",
                            })
                          : t("actions.submit", {
                              ns: "common",
                              defaultValue: "Send",
                            })
                    }
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      border: "none",
                      backgroundColor: listening
                        ? colors.primary
                        : disabledEmpty
                          ? "rgba(0,0,0,0.06)"
                          : "var(--text-primary)",
                      color: listening
                        ? "#ffffff"
                        : disabledEmpty
                          ? "var(--text-tertiary)"
                          : "var(--bg)",
                      cursor: disabledEmpty ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background-color 150ms, opacity 150ms",
                    }}
                    onMouseEnter={(e) => {
                      if (!disabledEmpty) e.currentTarget.style.opacity = "0.88";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                  >
                    <MaterialSymbol
                      name={
                        listening ? "stop" : showMic ? "mic" : "arrow_upward"
                      }
                      size={20}
                    />
                  </button>
                );
              })()}
            </div>
          </fieldset>
        )}
      </div>

      {parseError ? (
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: 13,
            lineHeight: "18px",
            textAlign: "center",
            marginTop: 12,
          }}
        >
          {parseFailedTitle}: {parseError}
        </p>
      ) : null}

      {shouldShowSecondaryAction ? (
        <button
          type="button"
          onClick={() => {
            trackProductEvent("resume_import_secondary_action_clicked", {
              context: analyticsContext,
            });
            onSecondaryAction();
          }}
          disabled={busy}
          style={{
            width: "100%",
            height: 40,
            borderRadius: 9999,
            backgroundColor: "transparent",
            color: "var(--text-secondary)",
            fontSize: 14,
            fontWeight: 500,
            border: "none",
            cursor: busy ? "default" : "pointer",
            fontFamily: "inherit",
            opacity: busy ? 0.35 : 1,
            marginTop: 16,
          }}
        >
          {secondaryActionLabel}
        </button>
      ) : null}
    </div>
  );
}

function newFormData(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}
